import { sortOptions, yearOptions } from "@/lib/movies";
import type { SortValue } from "@/lib/movies";

interface FilterValues {
  tag: string;
  year: string;
  minRating: string;
  sort: SortValue;
}

interface FilterBarProps {
  values: FilterValues;
  tags: string[];
  onChange: (patch: Partial<FilterValues>) => void;
  onReset: () => void;
}

const selectClass =
  "w-full h-10 pl-3 pr-9 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 appearance-none cursor-pointer focus:outline-none focus:border-primary-400";

export default function FilterBar({ values, tags, onChange, onReset }: FilterBarProps) {
  return (
    <div className="rounded-lg border border-background-200/70 bg-background-50/80 p-4 md:p-5">
      <div className="flex flex-wrap gap-1.5">
        {["全部题材", ...tags].map((tag) => {
          const value = tag === "全部题材" ? "all" : tag;
          const active = values.tag === value;
          return (
            <button
              key={tag}
              type="button"
              onClick={() => onChange({ tag: value })}
              className={`h-8 px-3 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                active
                  ? "bg-primary-500 text-background-50 font-medium"
                  : "bg-secondary-100/70 text-secondary-900 hover:bg-secondary-200/70"
              }`}
            >
              {tag}
            </button>
          );
        })}
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <label className="block">
          <span className="block text-[11px] text-foreground-500 mb-1.5">上映年份</span>
          <span className="relative block">
            <select
              value={values.year}
              onChange={(event) => onChange({ year: event.target.value })}
              className={selectClass}
            >
              <option value="all">全部年份</option>
              {yearOptions.map((year) => (
                <option key={year} value={String(year)}>
                  {year} 年
                </option>
              ))}
              <option value="2019">2019 年及更早</option>
            </select>
            <i
              className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500 pointer-events-none"
              aria-hidden="true"
            ></i>
          </span>
        </label>

        <label className="block">
          <span className="block text-[11px] text-foreground-500 mb-1.5">最低评分</span>
          <span className="relative block">
            <select
              value={values.minRating}
              onChange={(event) => onChange({ minRating: event.target.value })}
              className={selectClass}
            >
              <option value="all">全部评分</option>
              <option value="8.5">8.5 分以上</option>
              <option value="8">8.0 分以上</option>
              <option value="7.5">7.5 分以上</option>
            </select>
            <i
              className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500 pointer-events-none"
              aria-hidden="true"
            ></i>
          </span>
        </label>

        <label className="block">
          <span className="block text-[11px] text-foreground-500 mb-1.5">排序方式</span>
          <span className="relative block">
            <select
              value={values.sort}
              onChange={(event) => onChange({ sort: event.target.value as SortValue })}
              className={selectClass}
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <i
              className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500 pointer-events-none"
              aria-hidden="true"
            ></i>
          </span>
        </label>

        <div className="flex items-end">
          <button
            type="button"
            onClick={onReset}
            className="w-full h-10 rounded-md border border-background-300 text-foreground-700 text-sm flex items-center justify-center gap-1.5 hover:border-primary-400 hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}